import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CastError } from '../../../chromecast/cast-error';
import { Cast } from '../../../chromecast/hooks/use-cast';
import { useEventCallback } from '../../../hooks/use-event-callback';
import { PvrRecording } from '../../../interfaces';
import { PlayData } from '../../../queries/ats/playback/api';
import { SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { wrapAssetAsAltiboxAsset } from '../../../utils/altiboxassetUtils';
import { DetailsLink } from '../../../views/ats-details/details-link';
import Loading from '../../loading/loading';
import { ChromecastPoster } from '../ats-player/chromecast-poster';
import { castStream } from '../helpers/ChromeCast';
import { PlayerLayout } from '../player-layout';
import PlayerAssetCard from '../UI/PlayerAssetCard';
import { getRecordingStream } from './get-recording-stream';

interface ChromecastRecordingSenderProps {
  cast: Cast & { session: cast.framework.CastSession };
  data: PlayData;
  recording: SingleRecording;
  legacyRecording: PvrRecording;
  position: number | undefined;
}

export function ChromecastRecordingSender({
  cast,
  data,
  recording,
  legacyRecording,
  position = 0,
}: ChromecastRecordingSenderProps) {
  const auth = useSelector((state) => state.authReducer);
  const [error, setError] = useState<unknown>();

  const castRecording = useEventCallback(async () => {
    if (cast.mediaStatus === 'playing') {
      return;
    }

    try {
      const stream = getRecordingStream(recording, data, position);
      await castStream(cast.session, stream, auth, wrapAssetAsAltiboxAsset(legacyRecording));
    } catch (err) {
      setError(err);
    }
  });

  useEffect(() => {
    castRecording();
  }, [castRecording]);

  if (error) {
    const message = error instanceof Error ? error.message : JSON.stringify(error);
    throw new CastError(message, { sessionId: cast.session.getSessionId(), cause: error });
  }

  return (
    <PlayerLayout title={recording.name}>
      <ChromecastPoster src={recording.image} />

      {(cast.mediaStatus === 'loading' || cast.mediaStatus === 'buffering' || cast.mediaStatus === 'idle') && (
        <Loading classNames={{ container: 'spinner' }} size={82} contained />
      )}

      <DetailsLink className="player-asset-card-link" id={recording.programId} type="recording" subType="single">
        <PlayerAssetCard image={recording.image} currentTitle={recording.name} />
      </DetailsLink>
    </PlayerLayout>
  );
}
